import type { Card, Deck } from '@/types'
import { hasUnsavedDeckChanges, hasUnsavedNewDeckContent } from '@/utils/unsavedChanges'

export interface LocalDeckCard {
  card: Card
  quantity: number
}

/** Max copies of one base card (cardImageId) allowed across all printings in a deck. */
const MAX_COPIES_PER_BASE_CARD = 4

/** Add one copy of `card` (respects the 4-copy rule per base card). */
export function addCardCapped(prev: LocalDeckCard[], card: Card): LocalDeckCard[] {
  const totalBaseCount = prev
    .filter((lc) => lc.card.cardImageId === card.cardImageId)
    .reduce((sum, lc) => sum + lc.quantity, 0)
  if (totalBaseCount >= MAX_COPIES_PER_BASE_CARD) return prev
  const existing = prev.find((lc) => lc.card.id === card.id)
  if (existing) {
    return prev.map((lc) => (lc.card.id === card.id ? { ...lc, quantity: lc.quantity + 1 } : lc))
  }
  return [...prev, { card, quantity: 1 }]
}

/** Remove one copy of `card`, dropping the row when the last copy goes. */
export function removeOneCard(prev: LocalDeckCard[], card: Card): LocalDeckCard[] {
  const existing = prev.find((lc) => lc.card.id === card.id)
  if (!existing) return prev
  if (existing.quantity <= 1) return prev.filter((lc) => lc.card.id !== card.id)
  return prev.map((lc) => (lc.card.id === card.id ? { ...lc, quantity: lc.quantity - 1 } : lc))
}

export function buildDeckCardMap(localCards: LocalDeckCard[]): Map<number, LocalDeckCard> {
  const map = new Map<number, LocalDeckCard>()
  localCards.forEach((lc) => map.set(lc.card.id, lc))
  return map
}

/** Total copies per base card (cardImageId) for the 4-copy rule. */
export function buildBaseCardCounts(localCards: LocalDeckCard[]): Map<string, number> {
  const counts = new Map<string, number>()
  localCards.forEach((lc) => {
    const baseId = lc.card.cardImageId
    counts.set(baseId, (counts.get(baseId) || 0) + lc.quantity)
  })
  return counts
}

interface DraftFields {
  name: string
  description: string
  leaderId: number | null
  cards: LocalDeckCard[]
}

/**
 * Unsaved changes detection — delegated to shared util so other edit flows can
 * reuse the same comparison shape. New decks compare against "empty"; existing
 * decks compare the draft against the server deck + its card snapshot.
 */
export function computeHasUnsavedChanges(
  isEditing: boolean,
  draft: DraftFields,
  serverDeck: Deck | undefined,
  serverCards: LocalDeckCard[],
): boolean {
  if (!isEditing) {
    return hasUnsavedNewDeckContent(draft.name, draft.description, draft.cards)
  }
  return hasUnsavedDeckChanges(
    { metadata: { name: draft.name, description: draft.description, leaderId: draft.leaderId }, cards: draft.cards },
    {
      metadata: {
        name: serverDeck?.name || '',
        description: serverDeck?.description || '',
        leaderId: serverDeck?.leader.id || null,
      },
      cards: serverCards,
    },
  )
}

interface CardSyncPlan {
  /** Card ids on the server that the draft no longer has. */
  removals: number[]
  /** Cards to add (not on the server) or update (quantity differs), in draft order. */
  changes: Array<{ kind: 'add' | 'update'; cardId: number; quantity: number }>
}

/** Diff the draft cards against the last server snapshot into the minimal add/update/remove calls. */
export function planCardSync(serverCards: LocalDeckCard[], localCards: LocalDeckCard[]): CardSyncPlan {
  const serverMap = new Map(serverCards.map((c) => [c.card.id, c.quantity]))
  const localMap = new Map(localCards.map((c) => [c.card.id, c]))

  const removals = [...serverMap.keys()].filter((cardId) => !localMap.has(cardId))
  const changes: CardSyncPlan['changes'] = []
  for (const [cardId, lc] of localMap) {
    const serverQty = serverMap.get(cardId)
    if (serverQty === undefined) changes.push({ kind: 'add', cardId, quantity: lc.quantity })
    else if (serverQty !== lc.quantity) changes.push({ kind: 'update', cardId, quantity: lc.quantity })
  }
  return { removals, changes }
}
